import { Disclosure, Menu, MenuItem, MenuItems, Transition } from '@headlessui/react';
import { Bars3Icon, ChevronDownIcon, UserCircleIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { cssBundleHref } from '@remix-run/css-bundle';
import { json, type LinksFunction, type LoaderFunctionArgs, type MetaFunction } from '@remix-run/node';
import {
  Form,
  Link,
  Links,
  LiveReload,
  Meta,
  Outlet,
  Scripts,
  ScrollRestoration,
  useFetcher,
  useLoaderData,
  useMatches,
  useSearchParams } from
'@remix-run/react';
import React, { Fragment, type SVGProps, useEffect } from 'react';
import { HoneypotProvider } from 'remix-utils/honeypot/react';
import { GeneralErrorBoundary } from './components/error-boundary.tsx';
import { EpicProgress } from './components/progress-bar.tsx';
import { ReturnsLoader } from './components/returns/returns-loader.tsx';
import { useToast } from './components/toaster.tsx';
import tailwindStyleSheetUrl from './styles/tailwind.css';
import { getEnv } from './utils/env.server.ts';
import { honeypot } from './utils/honeypot.server.ts';
import { combineHeaders, getDomainUrl, isCurrentPage } from './utils/misc.tsx';
import { useNonce } from './utils/nonce-provider.ts';
import { makeTimings, time } from './utils/timing.server.ts';
import { getToast } from './utils/toast.server.ts';
import { useOptionalUser } from './utils/user.ts';
import classNames from 'classnames';
import {
  getCompletedReturnSizeCookie,
  getCookieConsentCookie,
  getMediaSizeCookie,
  setCompletedReturnSizeCookie,
  setCookieConsentCookie,
  setMediaSizeCookie } from
'#app/utils/media.server.ts';
import _ from 'lodash';
import { get } from '#app/utils/refundr.server.aragorn.ts';
import { type Client } from '#app/model/model.ts';
import { Toaster } from 'react-hot-toast';
import { getUserId, logout } from '#app/utils/session.server.ts';
import { withSentry } from '@sentry/remix';
import { CookieConsentProvider } from '#app/context/CookieConsentContext.tsx';
import CookieBanner from '#app/components/cookie-banner.tsx';
import { useChangeLanguage } from 'remix-i18next/react';
import { useTranslation } from 'react-i18next';
import { themeInitScript } from '#app/utils/theme.ts';
import { getDefaultHomePath } from '#app/utils/home-page.server.ts';
import { getLocaleCookie, getUrlLocale, setLocaleCookie } from '#app/utils/locale.server.ts';
import { type JSX } from 'react/jsx-runtime';

const publicNavigation = [
{ name: 'root.publicNavigation.home', href: '/', current: true },
{ name: 'root.publicNavigation.aboutUs', href: '/about-us', current: false },
{ name: 'root.publicNavigation.clientFeedback', href: '/client-feedback', current: false },
{ name: 'root.navigation.subscription', href: '/subscription/description', current: false }];


const navigation = [
{ name: 'root.navigation.taxReturns', href: '/returns', current: true },
{ name: 'root.navigation.receipts', href: '/receipts', current: false },
{ name: 'root.navigation.savings', href: '/savings', current: false },
{ name: 'root.navigation.documentPrep', href: '/document-prep', current: false },
{ name: 'root.navigation.taxConsultant', href: '/consultant', current: false },
{ name: 'root.navigation.subscription', href: '/subscription', current: false }];


const footerNavigation = {
  consultants: [
  {
    name: 'marketing.index.footer.sections.consultants.consultantTestimonials',
    href: '/client-feedback',
    blank: true
  }],

  support: [{ name: 'marketing.index.footer.sections.support.contactUs', href: 'mailto:info@refundr.ca' }],
  about: [
  { name: 'marketing.index.footer.sections.company.about', href: 'https://refundr.ca/about-us', blank: true },
  {
    name: 'marketing.index.footer.sections.consultants.sales',
    href: 'https://calendly.com/refundr-michael/30min',
    blank: true
  }],

  legal: [
  { name: 'marketing.index.footer.sections.legal.terms.service', href: 'https://refundr.ca/terms', blank: false },
  {
    name: 'marketing.index.footer.sections.legal.terms.privacy',
    href: 'https://refundr.ca/privacy-policy',
    blank: false
  },
  { name: 'marketing.index.footer.sections.legal.terms.cookies', href: '/cookie-settings' }],

  social: [
  {
    name: 'Facebook',
    href: '#',
    icon: (props: JSX.IntrinsicAttributes & SVGProps<SVGSVGElement>) =>
    <svg fill="currentColor" viewBox="0 0 24 24" {...props}>
					<path
        fillRule="evenodd"
        d="M22 12c0-5.523-4.477-10-10-10S2 6.477 2 12c0 4.991 3.657 9.128 8.438 9.878v-6.987h-2.54V12h2.54V9.797c0-2.506 1.492-3.89 3.777-3.89 1.094 0 2.238.195 2.238.195v2.46h-1.26c-1.243 0-1.63.771-1.63 1.562V12h2.773l-.443 2.89h-2.33v6.988C18.343 21.128 22 16.991 22 12z"
        clipRule="evenodd" />
      
				</svg>

  },
  {
    name: 'Instagram',
    href: '#',
    icon: (props: JSX.IntrinsicAttributes & SVGProps<SVGSVGElement>) =>
    <svg fill="currentColor" viewBox="0 0 24 24" {...props}>
					<path
        fillRule="evenodd"
        d="M12.315 2c2.43 0 2.784.013 3.808.06 1.064.049 1.791.218 2.427.465a4.902 4.902 0 011.772 1.153 4.902 4.902 0 011.153 1.772c.247.636.416 1.363.465 2.427.048 1.067.06 1.407.06 4.123v.08c0 2.643-.012 2.987-.06 4.043-.049 1.064-.218 1.791-.465 2.427a4.902 4.902 0 01-1.153 1.772 4.902 4.902 0 01-1.772 1.153c-.636.247-1.363.416-2.427.465-1.067.048-1.407.06-4.123.06h-.08c-2.643 0-2.987-.012-4.043-.06-1.064-.049-1.791-.218-2.427-.465a4.902 4.902 0 01-1.772-1.153 4.902 4.902 0 01-1.153-1.772c-.247-.636-.416-1.363-.465-2.427-.047-1.024-.06-1.379-.06-3.808v-.63c0-2.43.013-2.784.06-3.808.049-1.064.218-1.791.465-2.427a4.902 4.902 0 011.153-1.772A4.902 4.902 0 015.45 2.525c.636-.247 1.363-.416 2.427-.465C8.901 2.013 9.256 2 11.685 2h.63zm-.081 1.802h-.468c-2.456 0-2.784.011-3.807.058-.975.045-1.504.207-1.857.344-.467.182-.8.398-1.15.748-.35.35-.566.683-.748 1.15-.137.353-.3.882-.344 1.857-.047 1.023-.058 1.351-.058 3.807v.468c0 2.456.011 2.784.058 3.807.045.975.207 1.504.344 1.857.182.466.399.8.748 1.15.35.35.683.566 1.15.748.353.137.882.3 1.857.344 1.054.048 1.37.058 4.041.058h.08c2.597 0 2.917-.01 3.96-.058.976-.045 1.505-.207 1.858-.344.466-.182.8-.398 1.15-.748.35-.35.566-.683.748-1.15.137-.353.3-.882.344-1.857.048-1.055.058-1.37.058-4.041v-.08c0-2.597-.01-2.917-.058-3.96-.045-.976-.207-1.505-.344-1.858a3.097 3.097 0 00-.748-1.15 3.098 3.098 0 00-1.15-.748c-.353-.137-.882-.3-1.857-.344-1.023-.047-1.351-.058-3.807-.058zM12 6.865a5.135 5.135 0 110 10.27 5.135 5.135 0 010-10.27zm0 1.802a3.333 3.333 0 100 6.666 3.333 3.333 0 000-6.666zm5.338-3.205a1.2 1.2 0 110 2.4 1.2 1.2 0 010-2.4z"
        clipRule="evenodd" />
      
				</svg>

  },
  {
    name: 'X',
    href: '#',
    icon: (props: JSX.IntrinsicAttributes & SVGProps<SVGSVGElement>) =>
    <svg fill="currentColor" viewBox="0 0 24 24" {...props}>
					<path d="M13.6823 10.6218L20.2391 3H18.6854L12.9921 9.61788L8.44486 3H3.2002L10.0765 13.0074L3.2002 21H4.75404L10.7663 14.0113L15.5685 21H20.8131L13.6819 10.6218H13.6823ZM11.5541 13.0956L10.8574 12.0991L5.31391 4.16971H7.70053L12.1742 10.5689L12.8709 11.5655L18.6861 19.8835H16.2995L11.5541 13.096V13.0956Z" />
				</svg>

  },
  {
    name: 'GitHub',
    href: '#',
    icon: (props: JSX.IntrinsicAttributes & SVGProps<SVGSVGElement>) =>
    <svg fill="currentColor" viewBox="0 0 24 24" {...props}>
					<path
        fillRule="evenodd"
        d="M12 2C6.477 2 2 6.484 2 12.017c0 4.425 2.865 8.18 6.839 9.504.5.092.682-.217.682-.483 0-.237-.008-.868-.013-1.703-2.782.605-3.369-1.343-3.369-1.343-.454-1.158-1.11-1.466-1.11-1.466-.908-.62.069-.608.069-.608 1.003.07 1.531 1.032 1.531 1.032.892 1.53 2.341 1.088 2.91.832.092-.647.35-1.088.636-1.338-2.22-.253-4.555-1.113-4.555-4.951 0-1.093.39-1.988 1.029-2.688-.103-.253-.446-1.272.098-2.65 0 0 .84-.27 2.75 1.026A9.564 9.564 0 0112 6.844c.85.004 1.705.115 2.504.337 1.909-1.296 2.747-1.027 2.747-1.027.546 1.379.202 2.398.1 2.651.64.7 1.028 1.595 1.028 2.688 0 3.848-2.339 4.695-4.566 4.943.359.309.678.92.678 1.855 0 1.338-.012 2.419-.012 2.747 0 .268.18.58.688.482A10.019 10.019 0 0022 12.017C22 6.484 17.522 2 12 2z"
        clipRule="evenodd" />
      
				</svg>

  },
  {
    name: 'YouTube',
    href: '#',
    icon: (props: JSX.IntrinsicAttributes & SVGProps<SVGSVGElement>) =>
    <svg fill="currentColor" viewBox="0 0 24 24" {...props}>
					<path
        fillRule="evenodd"
        d="M19.812 5.418c.861.23 1.538.907 1.768 1.768C21.998 8.746 22 12 22 12s0 3.255-.418 4.814a2.504 2.504 0 0 1-1.768 1.768c-1.56.419-7.814.419-7.814.419s-6.255 0-7.814-.419a2.505 2.505 0 0 1-1.768-1.768C2 15.255 2 12 2 12s0-3.255.417-4.814a2.507 2.507 0 0 1 1.768-1.768C5.744 5 11.998 5 11.998 5s6.255 0 7.814.418ZM15.194 12 10 15V9l5.194 3Z"
        clipRule="evenodd" />
      
				</svg>

  }]

};

/**
 * https://remix.run/docs/en/main/route/links
 */
export const links: LinksFunction = () => {
  return [
  // Preload CSS as a resource to avoid render blocking
  { rel: 'preload', href: tailwindStyleSheetUrl, as: 'style' },
  cssBundleHref ? { rel: 'preload', href: cssBundleHref, as: 'style' } : null,
  { rel: 'mask-icon', href: '/favicons/favicon.png' },
  {
    rel: 'alternate icon',
    type: 'image/png',
    href: '/favicons/favicon.png'
  },
  { rel: 'apple-touch-icon', href: '/favicons/favicon.png' },
  {
    rel: 'manifest',
    href: '/site.webmanifest',
    crossOrigin: 'use-credentials'
  } as const, // necessary to make typescript happy
  //These should match the css preloads above to avoid css as render blocking resource
  { rel: 'icon', type: 'image/png', href: '/favicons/favicon.png' },
  { rel: 'stylesheet', href: tailwindStyleSheetUrl },
  cssBundleHref ? { rel: 'stylesheet', href: cssBundleHref } : null].
  filter(Boolean);
};

export const headers = () => ({
  'Cache-Control': 'no-cache, must-revalidate'
});

/**
 * https://remix.run/docs/en/main/route/meta#meta-function-parameters
 *
 * @param data
 */
export const meta: MetaFunction<typeof loader> = ({ data }) => {
  return [
  { title: 'refundr - Connect with Tax Professionals | Simplifing Tax Season' },
  {
    name: 'description',
    content:
    'refundr modernizes tax filing by connecting Canadians with trusted tax professionals. Secure document sharing, direct messaging, guided process, and e-filing support for better collaboration and results.'
  },
  {
    name: 'keywords',
    content:
    'tax filing Canada, connect with tax professional, Canadian tax platform, secure tax document sharing, efile tax return, tax consultant client portal, refundr'
  },
  {
    name: 'robots',
    content: 'index, follow'
  },
  { 'og:title': 'refundr - Simplifing Tax Season' },
  { 'og:type': 'website' },
  {
    'og:description':
    'Re-inventing how Canadians connect with tax experts. Secure, mobile-friendly platform for document management, communication, and simplified tax returns.'
  },
  { 'og:image': 'https://www.refundr.ca/img/refundr-white-og.png' },
  { 'og:url': 'https://www.refundr.ca' },
  { 'og:site': 'https://www.refundr.ca' }];

};

export async function loader({ request }: LoaderFunctionArgs) {
  const timings = makeTimings('root loader');
  const userId = await time(() => getUserId(request), {
    timings,
    type: 'getUserId',
    desc: 'getUserId in root'
  });

  const user = userId ?
  await time(() => get<Client>(request, `/clients/${userId}?detail=simple`), {
    timings,
    type: 'find user',
    desc: 'find user in root'
  }) :
  null;

  if (userId && !user) {
    console.info('something weird happened');
    // something weird happened... The user is authenticated but we can't find
    // them in the database. Maybe they were deleted? Let's log them out.
    await logout({ request, redirectTo: '/' });
  }

  const cookieLocale = getLocaleCookie(request);
  const urlLocale = getUrlLocale(request);
  const clientLocale =
  user?.preferredLocale === 'en' || user?.preferredLocale === 'fr' ? user.preferredLocale : undefined;
  // Logged-in preference, then explicit ?lang=, then cookie, then English
  const locale = clientLocale ?? urlLocale ?? cookieLocale ?? 'en';
  const shouldSetLocaleCookie = cookieLocale !== locale;
  const { toast, headers: toastHeaders } = await getToast(request);
  const honeyProps = honeypot.getInputProps();
  const mediaSize = getMediaSizeCookie(request);
  const createdMediaSizeCookie = setMediaSizeCookie('md');
  const completedReturnSizeCookie = getCompletedReturnSizeCookie(request);
  const createdCompletedReturnSizeCookie = setCompletedReturnSizeCookie('xl');
  const cookieConsent = getCookieConsentCookie(request);
  const createdCookieConsent = setCookieConsentCookie('unknown');
  const defaultHomePath = userId ? getDefaultHomePath(request) : '/';

  return json(
    {
      user,
      cookieConsent,
      defaultHomePath,
      requestInfo: {
        origin: getDomainUrl(request),
        path: new URL(request.url).pathname
      },
      ENV: getEnv(),
      toast,
      honeyProps,
      locale
    },
    {
      headers: combineHeaders(
        { 'Server-Timing': timings.toString() },
        toastHeaders,
        !mediaSize ? { 'set-cookie': createdMediaSizeCookie } : null,
        !completedReturnSizeCookie ? { 'set-cookie': createdCompletedReturnSizeCookie } : null,
        !cookieConsent ? { 'set-cookie': createdCookieConsent } : null,
        shouldSetLocaleCookie ? { 'set-cookie': setLocaleCookie(locale) } : null
      )
    }
  );
}

export const handle = {
  // In the handle export, we can add a i18n key with namespaces our route
  // will need to load. This key can be a single string or an array of strings.
  // TIP: In most cases, you should set this to your defaultNS from your i18n config
  // or if you did not set one, set it to the i18next default namespace "translation"
  i18n: 'common'
};

function Document({
  children,
  nonce,
  locale = 'en',
  i18n,
  env = {}






}: {children: React.ReactNode;nonce: string;locale: string;i18n: any;env?: Record<string, string>;}) {
  return (
    <html lang={locale} dir={i18n.dir()} className={`h-full overflow-x-hidden`} suppressHydrationWarning>
			<head>
				<script dangerouslySetInnerHTML={{ __html: themeInitScript }} />
				<script async src="https://www.googletagmanager.com/gtag/js?id=G-P95QDW7M6X"></script>
				<script
          dangerouslySetInnerHTML={{
            __html: `
						
						// Function to get cookie value by name
						  function getCookie(name) {
							const value = "; " + document.cookie;
							const parts = value.split("; " + name + "=");
							if (parts.length === 2) return parts.pop().split(";").shift();
							return null;
						  }
						
						// Check if consent is given
						  const consent = getCookie('refundr-cookie-consent');
						  if (consent === 'accepted') {
							// Load Google Analytics
							(function() {
							  const script = document.createElement('script');
							  script.async = true;
							  script.src = 'https://www.googletagmanager.com/gtag/js?id=G-P95QDW7M6X';
							  document.head.appendChild(script);
					
							  window.dataLayer = window.dataLayer || [];
							  function gtag() { dataLayer.push(arguments); }
							  gtag('js', new Date());
							  gtag('config', 'G-P95QDW7M6X');
							})();
						  }
						`
          }}>
        </script>
				<Meta />
				<meta charSet="utf-8" />
				<meta name="viewport" content="width=device-width,initial-scale=1" />
				{/* OG Meta Tags to improve the way the post looks when you share the page on LinkedIn, Facebook, Google+ */}
				<meta content="refundr.ca" property="og:site_name" />
				<meta content="https://www.refundr.ca" property="og:site" />
				<meta content="refundr" property="og:title" />
				<meta content="Tax return and client management software" property="og:description" />
				<meta content="https://www.refundr.ca/img/refundr.jpg" property="og:image" />
				<meta content="https://www.refundr.ca" property="og:url" />
				<meta content="article" property="og:type" />
				<Links />
			</head>
			<body className="bg-background text-foreground">
				{children}
				<script
          nonce={nonce}
          dangerouslySetInnerHTML={{
            __html: `window.ENV = ${JSON.stringify(env)}`
          }} />
        
				<ScrollRestoration nonce={nonce} />
				<Scripts nonce={nonce} />
				<LiveReload nonce={nonce} />
			</body>
		</html>);

}

function App() {
  const matches = useMatches();
  const data = useLoaderData<typeof loader>();
  const nonce = useNonce();
  const optionalUser = useOptionalUser();
  const { i18n, t } = useTranslation();
  useChangeLanguage(data.locale as string); // Synchronize client i18n instance

  const fetcher = useFetcher(); // For submitting language change

  const handleChangeLanguage = (newLocale: string) => {
    if (newLocale === data.locale) return;
    // Drop any stale in-memory locale bundles so newly added keys (e.g. documentPrep) load
    void i18n.reloadResources(newLocale, 'common').finally(() => {
      fetcher.submit({ locale: newLocale }, { method: 'post', action: '/change-language' });
    });
  };

  // const isOnSearchPage = matches.find(m => m.id === 'routes/users+/index')
  // const searchBar = isOnSearchPage ? null : <SearchBar status="idle"/>
  useToast(data.toast);

  const [searchParams] = useSearchParams();
  const agentFriendlyParam = searchParams.get('friendlyId');
  useEffect(() => {
    const agent_friendly_id = window.localStorage.getItem('agent_friendly_id');
    if (_.isEmpty(agent_friendly_id)) {
      window.localStorage.setItem('agent_friendly_id', agentFriendlyParam ?? '');
    }
  }, [agentFriendlyParam]);

  return (
    <Document nonce={nonce} env={data.ENV} locale={data.locale as string} i18n={i18n}>
			<div className="flex h-screen flex-col justify-between">
				<div className="min-h-full">
					{/* NAV START */}
					<Disclosure as="nav" className="border-b border-gray-200 bg-white shadow-sm">
						{({ open }) =>
            <>
								<div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
									<div className="flex h-20 items-center justify-between gap-4">
										<div className="flex min-w-0 flex-1 items-center">
											{/* LOGO START */}
											<div className="flex flex-shrink-0 items-center">
												<Link to={data.defaultHomePath} onClick={() => isCurrentPage(navigation, null)}>
													<img className="block h-6 w-auto lg:hidden" src="/img/logo/logo-black.png" alt="refundr" />
												</Link>
												<Link to={data.defaultHomePath} onClick={() => isCurrentPage(navigation, null)}>
													<img className="hidden h-6 w-auto lg:block" src="/img/logo/logo-black.png" alt="refundr" />
												</Link>
											</div>
											{/* LOGO END */}

											{/* PUBLIC NAV START */}
											{!data.user &&
                    <div className="hidden lg:-my-px lg:ml-8 lg:flex lg:items-center lg:gap-x-5 xl:ml-12 xl:gap-x-6">
													{publicNavigation.map((item) =>
                      <Link
                        key={item.name}
                        to={item.href}
                        onClick={() => isCurrentPage(navigation, item.name)}
                        className={classNames(
                          'inline-flex items-center whitespace-nowrap border-b-2 border-transparent px-1 pt-1 text-sm font-medium text-gray-500 hover:border-gray-300 hover:text-blue-700'
                        )}
                        aria-current={item.current ? 'page' : undefined}>
                        
															{t(item.name)}
														</Link>
                      )}
												</div>
                    }
											{/* PUBLIC NAV END */}

											{/* SITE NAV START */}
											{data.user &&
                    <div className="hidden min-w-0 lg:-my-px lg:ml-6 lg:flex lg:items-center lg:gap-x-4 xl:ml-10 xl:gap-x-5">
													{navigation.
                      filter((item) => {
                        // Skip subscription tab if user doesn't need it
                        if (item.name === 'root.navigation.subscription') {
                          return data?.user?.agencyRequiresSubscription;
                        }
                        return true; // show all other items
                      }).
                      map((item) =>
                      <Link
                        key={item.name}
                        to={item.href}
                        onClick={() => isCurrentPage(navigation, item.name)}
                        className={classNames(
                          item.current ?
                          'border-neutral-900 text-gray-900' :
                          'border-transparent text-gray-500 hover:border-gray-300 hover:text-blue-700',
                          'inline-flex items-center whitespace-nowrap border-b-2 px-1 pt-1 text-sm font-medium'
                        )}
                        aria-current={item.current ? 'page' : undefined}>
                        
																{t(item.name)}
															</Link>
                      )}
												</div>
                    }
											{/* SITE NAV END */}
										</div>

										{/* USER NAV START */}
										{optionalUser ?
                  <div className="hidden flex-shrink-0 lg:ml-4 lg:flex lg:items-center">
												<Menu as="div" className="relative">
													<div>
														<Menu.Button className="inline-flex items-center gap-1.5 rounded-full border border-gray-200 bg-white px-3 py-1.5 text-sm font-semibold text-gray-700 transition-colors hover:bg-gray-100 hover:text-gray-900 focus:outline-none focus:ring-2 focus:ring-neutral-900 focus:ring-offset-2 data-[headlessui-state=open]:bg-gray-100">
															<UserCircleIcon className="h-5 w-5 text-gray-500" aria-hidden="true" />
															{t('root.menu.label')}
															<ChevronDownIcon className="h-4 w-4 text-gray-400" aria-hidden="true" />
														</Menu.Button>
													</div>
													<Transition
                        as={Fragment}
                        enter="transition ease-out duration-200"
                        enterFrom="transform opacity-0 scale-95"
                        enterTo="transform opacity-100 scale-100"
                        leave="transition ease-in duration-75"
                        leaveFrom="transform opacity-100 scale-100"
                        leaveTo="transform opacity-0 scale-95">
                        
														<MenuItems className="absolute right-0 z-10 mt-2 w-56 origin-top-right rounded-md bg-white py-1 shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
															<MenuItem>
																<Link
                              to="/profile"
                              className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">
                              
																	{t('root.navigation.profile')}
																</Link>
															</MenuItem>
															<MenuItem>
																<Link
                              to="/feedback"
                              className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">
                              
																	{t('root.navigation.feedback')}
																</Link>
															</MenuItem>
															<div className="my-1 border-t border-gray-100" />
															<MenuItem>
																<button
                              type="button"
                              className={classNames(
                                'block w-full px-4 py-2 text-left text-sm hover:bg-gray-100',
                                data.locale === 'en' ? 'font-semibold text-gray-900' : 'text-gray-700'
                              )}
                              onClick={() => handleChangeLanguage('en')}>
                              
																	English
																</button>
															</MenuItem>
															<MenuItem>
																<button
                              type="button"
                              className={classNames(
                                'block w-full px-4 py-2 text-left text-sm hover:bg-gray-100',
                                data.locale === 'fr' ? 'font-semibold text-gray-900' : 'text-gray-700'
                              )}
                              onClick={() => handleChangeLanguage('fr')}>
                              
																	Français
																</button>
															</MenuItem>
															<div className="my-1 border-t border-gray-100" />
															<MenuItem>
																<Form action="/logout" method="POST">
																	<button className="block w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-100" type="submit">
																		{t('root.menu.logout')}
																	</button>
																</Form>
															</MenuItem>
														</MenuItems>
													</Transition>
												</Menu>
											</div> :

                  <div className="hidden flex-shrink-0 lg:ml-4 lg:flex lg:items-center lg:gap-4">
												<Link to="/login" className="block text-sm font-semibold text-gray-700 hover:text-blue-700">
													{t('root.menu.logIn')}
												</Link>
												<div className="hidden lg:flex lg:items-center lg:gap-3">
													{data.locale === 'en' ?
                      <button
                        className="block text-sm font-semibold text-gray-700 hover:text-blue-700"
                        onClick={() => handleChangeLanguage('fr')}>
                        
															FR
														</button> :

                      <button
                        className="block text-sm font-semibold text-gray-700 hover:text-blue-700"
                        onClick={() => handleChangeLanguage('en')}>
                        
															EN
														</button>
                      }
												</div>
											</div>
                  }

										{/* USER NAV END */}

										{/* NAV ICON START */}
										<div className="-mr-2 flex flex-shrink-0 items-center lg:hidden">
											<Disclosure.Button className="relative inline-flex items-center justify-center rounded-md bg-white p-2 text-gray-400 hover:bg-gray-100 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-neutral-900 focus:ring-offset-2">
												<span className="absolute -inset-0.5" />
												<span className="sr-only">Open main menu</span>
												{open ?
                      <XMarkIcon className="block h-6 w-6" aria-hidden="true" /> :

                      <Bars3Icon className="block h-6 w-6" aria-hidden="true" />
                      }
											</Disclosure.Button>
										</div>
										{/* NAV ICON END */}
									</div>
								</div>

								{/* MOBILE MENU START */}
								<Disclosure.Panel className="lg:hidden">
									<div className="space-y-1 pb-3 pt-2">
										{(data.user ? navigation : publicNavigation).
                  filter((item) => {
                    if (!data.user) return true;
                    if (item.name === 'root.navigation.subscription') {
                      return data?.user?.agencyRequiresSubscription;
                    }
                    return true;
                  }).
                  map((item) =>
                  <Disclosure.Button
                    key={item.name}
                    as="a"
                    href={item.href}
                    className={classNames(
                      item.current ?
                      'border-neutral-900 bg-neutral-100 text-neutral-900' :
                      'border-transparent text-gray-600 hover:border-gray-300 hover:bg-gray-50 hover:text-gray-800',
                      'block border-l-4 py-2 pl-3 pr-4 text-base font-medium'
                    )}
                    aria-current={item.current ? 'page' : undefined}>
                    
												{t(item.name)}
											</Disclosure.Button>
                  )}
									</div>
									{/* MOBILE NAV END */}

									{/* MOBILE USER NAV START */}
									{optionalUser ?
                <div className="border-t border-gray-200 pb-3 pt-4">
											<div className="space-y-1">
												<Link
                      to="/profile"
                      className="block px-4 py-2 text-base font-medium text-gray-500">
                      
													{t('root.navigation.profile')}
												</Link>
												<Link
                      to="/feedback"
                      className="block px-4 py-2 text-base font-medium text-gray-500">
                      
													{t('root.navigation.feedback')}
												</Link>
												<button
                      type="button"
                      className={classNames(
                        'block w-full px-4 py-2 text-left text-base font-medium',
                        data.locale === 'en' ? 'text-gray-900' : 'text-gray-500'
                      )}
                      onClick={() => handleChangeLanguage('en')}>
                      
													English
												</button>
												<button
                      type="button"
                      className={classNames(
                        'block w-full px-4 py-2 text-left text-base font-medium',
                        data.locale === 'fr' ? 'text-gray-900' : 'text-gray-500'
                      )}
                      onClick={() => handleChangeLanguage('fr')}>
                      
													Français
												</button>
												<Form action="/logout" method="POST">
													<button className="block px-4 py-2 text-base font-medium text-gray-500" type="submit">
														{t('root.menu.logout')}
													</button>
												</Form>
											</div>
										</div> :

                <div className="border-t border-gray-200 pb-3 pt-4">
											<Link to="/login" className="block px-4 py-2 text-base font-medium text-gray-500">
												{t('root.menu.logIn')}
											</Link>
											<div className="flex items-center gap-3 px-4 py-2 text-base font-medium">
												{data.locale === 'en' ?
                    <button
                      className="block text-sm font-semibold text-gray-600 hover:text-gray-800"
                      onClick={() => handleChangeLanguage('fr')}>
                      
														FR
													</button> :

                    <button
                      className="block text-sm font-semibold text-gray-600 hover:text-gray-800"
                      onClick={() => handleChangeLanguage('en')}>
                      
														EN
													</button>
                    }
											</div>
										</div>
                }
									{/* MOBILE USER NAV END */}
								</Disclosure.Panel>
								{/* MOBILE MENU END */}
							</>
            }
					</Disclosure>
					{/* NAV END */}

					{/* MAIN DIV START */}
					<div className="min-h-full">
						{/*<div className="min-h-full bg-[#F0F0F0] py-5">*/}
						{/*<div className="min-h-full bg-[#132029] py-5">*/}
						{/* <div className="py-10 min-h-full bg-[#132029]"> */}
						<main>
							<div className="mx-auto max-w-3xl px-6 sm:px-0">
								<nav className="flex" aria-label="Breadcrumb">
									<ol role="list" className="flex items-center space-x-4">
										{matches.
                    filter(
                      (match) =>
                      // @ts-ignore
                      match.handle && match.handle.breadcrumb
                    ).
                    map((match, index) =>
                    // @ts-ignore
                    <li key={index}>{match.handle.breadcrumb(match)}</li>
                    )}
									</ol>
								</nav>
							</div>
							<CookieConsentProvider>
								<Outlet key={data.locale as string} />
								<ReturnsLoader />
								{data.cookieConsent === 'unknown' && <CookieBanner />}
							</CookieConsentProvider>
						</main>
					</div>

					<footer className="bg-white">
						<div className="mx-auto max-w-7xl px-6 pb-8 pt-16 sm:pt-24 lg:px-8 lg:pt-32">
							<div className="xl:grid xl:grid-cols-3 xl:gap-8">
								<div className="space-y-8">
									<img alt="refundr inc." src="/img/logo/logo-black.png" className="h-9" />
									<p className="text-balance text-sm/6 text-gray-600">{t('marketing.index.footer.tagline')}</p>
									{/*<div className="flex gap-x-6">*/}
									{/*	{navigation.social.map(item => (*/}
									{/*		<a key={item.name} href={item.href} className="text-gray-600 hover:text-gray-800">*/}
									{/*			<span className="sr-only">{item.name}</span>*/}
									{/*			<item.icon aria-hidden="true" className="size-6" />*/}
									{/*		</a>*/}
									{/*	))}*/}
									{/*</div>*/}
								</div>
								<div className="mt-16 grid grid-cols-2 gap-8 xl:col-span-2 xl:mt-0">
									<div className="md:grid md:grid-cols-2 md:gap-8">
										<div>
											<h3 className="text-sm/6 font-semibold text-gray-900">
												{t('marketing.index.footer.sections.consultants.title')}
											</h3>
											<ul role="list" className="mt-6 space-y-4">
												{footerNavigation.consultants.map((item) =>
                        <li key={item.name}>
														<a
                            href={item.href}
                            className="text-sm/6 text-gray-600 hover:text-gray-900"
                            target={item.blank ? '_blank' : '_self'}>
                            
															{t(item.name)}
														</a>
													</li>
                        )}
											</ul>
										</div>
										<div className="mt-10 md:mt-0">
											<h3 className="text-sm/6 font-semibold text-gray-900">
												{t('marketing.index.footer.sections.support.title')}
											</h3>
											<ul role="list" className="mt-6 space-y-4">
												{footerNavigation.support.map((item) =>
                        <li key={item.name}>
														<a href={item.href} className="text-sm/6 text-gray-600 hover:text-gray-900">
															{t(item.name)}
														</a>
													</li>
                        )}
											</ul>
										</div>
									</div>
									<div className="md:grid md:grid-cols-2 md:gap-8">
										<div>
											<h3 className="text-sm/6 font-semibold text-gray-900">
												{t('marketing.index.footer.sections.company.title')}
											</h3>
											<ul role="list" className="mt-6 space-y-4">
												{footerNavigation.about.map((item) =>
                        <li key={item.name}>
														<a href={item.href} className="text-sm/6 text-gray-600 hover:text-gray-900">
															{t(item.name)}
														</a>
													</li>
                        )}
											</ul>
										</div>
										<div className="mt-10 md:mt-0">
											<h3 className="text-sm/6 font-semibold text-gray-900">
												{t('marketing.index.footer.sections.legal.title')}
											</h3>
											<ul role="list" className="mt-6 space-y-4">
												{footerNavigation.legal.map((item) =>
                        <li key={item.name}>
														<a href={item.href} className="text-sm/6 text-gray-600 hover:text-gray-900">
															{t(item.name)}
														</a>
													</li>
                        )}
											</ul>
										</div>
									</div>
								</div>
							</div>
							<div className="mt-16 border-t border-gray-900/10 pt-8 sm:mt-20 lg:mt-24">
								<p className="text-sm/6 text-gray-600">&copy; {t('marketing.index.footer.copyright')}</p>
							</div>
						</div>
					</footer>
				</div>
			</div>
			<div>
				<Toaster
          position="top-center"
          reverseOrder={false}
          containerStyle={{
            marginTop: '0px',
            marginLeft: '5px',
            marginRight: '5px'
          }} />
        
			</div>
			<EpicProgress />
		</Document>);

}

function AppWithProviders() {
  const data = useLoaderData<typeof loader>();
  return (
    <HoneypotProvider {...data.honeyProps}>
			<App />
		</HoneypotProvider>);

}

export default withSentry(AppWithProviders);

export function ErrorBoundary() {
  // the nonce doesn't rely on the loader so we can access that
  const nonce = useNonce();
  const { i18n } = useTranslation();

  // NOTE: you cannot use useLoaderData in an ErrorBoundary because the loader
  // likely failed to run so we have to do the best we can.
  // We could probably do better than this (it's possible the loader did run).
  // This would require a change in Remix.

  // Just make sure your root route never errors out and you'll always be able
  // to give the user a better UX.

  return (
    // @ts-ignore
    <Document nonce={nonce} i18n={i18n}>
			<GeneralErrorBoundary />
		</Document>);

}